import { isEmpty, isNil, isNumber, trim } from 'lodash'
import { isValidIBAN } from './validatorUtils'
import type { Identifier } from '@/shared/models/debtorModels'
import { EMPTY_VALUE } from '@hello-aria/ui/empty-value'

export const removeWhiteSpace = (value: string | number): string => {
    if (isNumber(value)) {
        return value.toString().replace(/ /g, '')
    }

    return value.replace(/ /g, '')
}

export const areStringEquals = (first: string | undefined, second: string | undefined): boolean => {
    if (isNil(first) || isNil(second)) {
        return false
    }

    const sanitizedFirst = removeWhiteSpace(first).toLocaleLowerCase()
    const sanitizedSecond = removeWhiteSpace(second).toLocaleLowerCase()

    return sanitizedFirst === sanitizedSecond
}

export interface TruncateOptions {
    position: 'start' | 'end'
}

export const truncate = (
    value: string | null | undefined,
    length: number,
    options?: TruncateOptions
): string | undefined => {
    const { position } = options || {
        position: 'start'
    }

    if (isNil(value)) return undefined

    if (position === 'start') return value.length <= length ? value : value.slice(0, length) + '...'
    return value.length <= length ? value : '...' + value.slice(value.length - length, value.length)
}

/**
 * @params string to check
 * @returns true if the value is an empty string or only containing trailing whitespace, false otherwise
 */
export const isEmptyString = (value: string | undefined | null): boolean => {
    return isNil(value) || isEmpty(trim(value))
}

/**
 * Masks a string, keeping a specified number of characters at the start and end, replacing the middle with three dots ("...").
 * @param value The string to mask
 * @param startMask Number of characters to keep at the start
 * @param endMask Number of characters to keep at the end
 * @returns The masked string
 */
export const maskSensitiveString = (value: string, startMask: number, endMask: number): string => {
    if (!value || value.length <= startMask + endMask) return value
    return value.slice(0, startMask) + '•••' + value.slice(value.length - endMask)
}

/**
 * Masks an account number or IBAN, keeping only the first/last characters visible.
 * IBAN: keep first 4, last 3. Others: keep first 2, last 2.
 * @param value The string to mask
 * @returns The masked string
 */
export const maskAccountNumber = (value: string): string => {
    if (isValidIBAN(value)) {
        return maskSensitiveString(value, 4, 3)
    }
    return maskSensitiveString(value, 2, 2)
}

export const formatIdentifier = (id: Identifier | undefined | null) => {
    if (!id) return EMPTY_VALUE
    const label = `${id.type.toUpperCase()} - ${id.value}`
    return isNil(id.country) ? label : `${label} (${id.country})`
}
